import { translateLabel } from "@/i18n/messages";
import React from "react";
import {
  ArrowSquareOutIcon,
  CableCarIcon,
  CheckCircleIcon,
  WarningCircleIcon,
} from "@phosphor-icons/react";
import { statusTrustSignal } from "../lib/statusTrust";
import { destinationDecisionSnapshot } from "../lib/destinationPresentation";

const TONE = {
  open: {
    border: "border-emerald-400/20",
    bg: "bg-emerald-400/[0.06]",
    text: "text-emerald-300",
    dot: "bg-emerald-400",
  },
  closed: {
    border: "border-red-400/25",
    bg: "bg-red-400/[0.07]",
    text: "text-red-300",
    dot: "bg-red-400",
  },
  restricted: {
    border: "border-amber-400/25",
    bg: "bg-amber-400/[0.07]",
    text: "text-amber-300",
    dot: "bg-amber-400",
  },
  unknown: {
    border: "border-white/10",
    bg: "bg-white/[0.035]",
    text: "text-zinc-300",
    dot: "bg-zinc-500",
  },
};

function StatusTrustCard({
  label,
  status,
  verification,
  secondary,
  icon: Icon = CheckCircleIcon,
  testId,
}) {
  const signal = statusTrustSignal(status, verification);
  const tone = TONE[signal.tone] || TONE.unknown;

  return (
    <div
      data-testid={testId}
      className={"rounded-xl border p-3.5 " + tone.border + " " + tone.bg}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-2.5">
          <span className={"mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full " + tone.dot} />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-500">
              <Icon size={13} /> {translateLabel(label)}
            </div>
            <div className={"mt-1 text-sm font-extrabold " + tone.text}>{translateLabel(signal.label)}</div>
            {secondary && (
              <div className="mt-1 text-[11px] leading-snug text-zinc-400">{secondary}</div>
            )}
          </div>
        </div>
        {signal.sourceUrl && (
          <a
            href={signal.sourceUrl}
            target="_blank"
            rel="noreferrer"
            onClick={(event) => event.stopPropagation()}
            className="flex shrink-0 items-center gap-1 text-[10px] font-bold text-[#CCFF00] hover:text-white"
            data-testid={testId + "-source"}
          >
            {translateLabel("Source")} <ArrowSquareOutIcon size={12} />
          </a>
        )}
      </div>

      <div className={"mt-2 flex items-center gap-1.5 text-[10px] font-semibold " + (
        signal.uncertain ? "text-amber-300" : "text-zinc-500"
      )}>
        {signal.uncertain
          ? <WarningCircleIcon size={12} weight="fill" />
          : <CheckCircleIcon size={12} weight="fill" className="text-emerald-400" />}
        <span>{translateLabel(signal.freshness.label)}</span>
      </div>

      {signal.warning && (
        <p
          data-testid={testId + "-warning"}
          className={"mt-2 text-[11px] leading-snug " + (
            signal.status === "CLOSED"
              ? "text-red-200"
              : signal.uncertain || signal.status === "RESTRICTED"
                ? "text-amber-200"
                : "text-zinc-400"
          )}
        >
          {signal.warning}
        </p>
      )}
    </div>
  );
}

const currentAccessMeta = (state) => {
  if (state === "OPEN") return { label: "Open today", tone: "open" };
  if (state === "OPEN_WITH_CONDITIONS") return { label: "Open with conditions", tone: "open" };
  if (state === "CLOSED_TO_PUBLIC") return { label: "Closed to public today", tone: "closed" };
  return null;
};

const currentParkStatusMeta = (status) => {
  if (status === "OPEN_NOW") return { label: "Open now", tone: "open" };
  if (status === "CLOSED_NOW") return { label: "Closed now", tone: "closed" };
  if (status === "RESTRICTED") return { label: "Restricted", tone: "restricted" };
  return { label: "Hours unknown", tone: "unknown" };
};

function CurrentParkStatusCard({ destination }) {
  const legal = destination?.visit_info?.legal_access;
  const access = currentAccessMeta(legal?.current_state);
  const current = access || currentParkStatusMeta(destination?.current_status);
  const tone = TONE[current.tone] || TONE.unknown;
  const reason = access ? legal?.current_state_reason : destination?.current_status_reason;
  const fee = legal?.fee;
  const feeLabel = fee
    ? `${fee.amount} ${fee.currency}${fee.payment_method ? ` · ${fee.payment_method}` : ""}`
    : null;

  return (
    <div
      data-testid="park-status-trust"
      className={"rounded-xl border p-3.5 " + tone.border + " " + tone.bg}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-2.5">
          <span className={"mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full " + tone.dot} />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-500">
              <CheckCircleIcon size={13} /> {translateLabel(access ? "Park access" : "Park status")}
            </div>
            <div className={"mt-1 text-sm font-extrabold " + tone.text}>{translateLabel(current.label)}</div>
            {reason && <div className="mt-1 text-[11px] leading-snug text-zinc-400">{translateLabel(reason)}</div>}
            {access && feeLabel && legal?.current_state !== "CLOSED_TO_PUBLIC" && (
              <div className="mt-1 text-[11px] font-semibold text-zinc-300">{feeLabel}</div>
            )}
          </div>
        </div>
        {access && legal?.source_url && (
          <a
            href={legal.source_url}
            target="_blank"
            rel="noreferrer"
            className="flex shrink-0 items-center gap-1 text-[10px] font-bold text-[#CCFF00] hover:text-white"
          >
            {translateLabel("Official")} <ArrowSquareOutIcon size={12} />
          </a>
        )}
      </div>
    </div>
  );
}

function ScheduledUpliftCard({ uplift, upliftCapability }) {
  const status = uplift?.current_status;
  const reason = uplift?.current_status_reason;
  const open = status === "OPEN_NOW";
  const closed = status === "CLOSED_NOW";
  const tone = open ? TONE.open : closed ? TONE.unknown : TONE.unknown;
  const noLiftToday = closed && reason === "No lift operation is scheduled today";
  const label = translateLabel(open ? "Lift open" : noLiftToday ? "No lift today" : closed ? "Lift closed" : "Status unknown");
  const capabilityDetail = upliftCapability.detail ? ` · ${upliftCapability.detail}` : "";

  return (
    <div data-testid="uplift-status-trust" className={"rounded-xl border p-3.5 " + tone.border + " " + tone.bg}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-2.5">
          <span className={"mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full " + (open ? TONE.open.dot : "bg-zinc-500")} />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-500">
              <CableCarIcon size={13} /> {translateLabel("Uplift")}
            </div>
            <div className={"mt-1 text-sm font-extrabold " + (open ? TONE.open.text : "text-zinc-300")}>{label}</div>
            {reason && <div className="mt-1 text-[11px] leading-snug text-zinc-400">{reason}</div>}
            {!open && (
              <div className="mt-1 text-[11px] leading-snug text-zinc-500">
                {translateLabel(upliftCapability.value)}{capabilityDetail ? ` · ${translateLabel(upliftCapability.detail)}` : ""}
              </div>
            )}
          </div>
        </div>
        {uplift?.source_url && (
          <a href={uplift.source_url} target="_blank" rel="noreferrer" className="flex shrink-0 items-center gap-1 text-[10px] font-bold text-[#CCFF00] hover:text-white">
            Official <ArrowSquareOutIcon size={12} />
          </a>
        )}
      </div>
    </div>
  );
}

function UpliftCapabilityCard({ upliftCapability }) {
  const tone = TONE.unknown;
  const detail = upliftCapability.detail ? " · " + upliftCapability.detail : "";

  return (
    <div
      data-testid="uplift-status-trust"
      className={"rounded-xl border p-3.5 " + tone.border + " " + tone.bg}
    >
      <div className="flex items-start gap-2.5">
        <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full bg-zinc-500" />
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-500">
            <CableCarIcon size={13} /> {translateLabel("Uplift")}
          </div>
          <div className="mt-1 text-sm font-extrabold text-zinc-300">{translateLabel("No mechanical uplift")}</div>
          <div className="mt-1 text-[11px] leading-snug text-zinc-400">
            {upliftCapability.value}{detail}
          </div>
        </div>
      </div>
    </div>
  );
}

export function DestinationStatusTrustPanel({ destination, accessLabel }) {
  const visit = destination?.visit_info;
  const uplift = visit?.uphill_access;
  const upliftCapability = destinationDecisionSnapshot(destination, { accessLabel }).uplift;
  const upliftVerification = uplift?.operational_status_verification
    ? {
        ...uplift.operational_status_verification,
        source: uplift.operational_status_verification.source
          || (uplift?.source_url ? { url: uplift.source_url, label: "Official uplift source" } : undefined),
      }
    : uplift?.source_url
      ? { checked_at: null, source: { url: uplift.source_url, label: "Official uplift source" } }
      : undefined;
  const capabilityDetail = upliftCapability.detail
    ? " · " + upliftCapability.detail
    : "";
  const noMechanicalUplift = uplift?.uplift_kind === "NONE"
    || uplift?.uplift_relationship === "NOT_APPLICABLE";

  return (
    <div data-testid="destination-status-trust-panel" className="space-y-2.5">
      <CurrentParkStatusCard destination={destination} />

      {noMechanicalUplift ? (
        <UpliftCapabilityCard upliftCapability={upliftCapability} />
      ) : uplift?.current_status ? (
        <ScheduledUpliftCard uplift={uplift} upliftCapability={upliftCapability} />
      ) : (
        <StatusTrustCard
          label={translateLabel("Uplift status")}
          status={uplift?.operational_status || "UNKNOWN"}
          verification={upliftVerification}
          secondary={translateLabel("Capability: {value}{detail}", { value: translateLabel(upliftCapability.value), detail: capabilityDetail ? ` · ${translateLabel(upliftCapability.detail)}` : "" })}
          icon={CableCarIcon}
          testId="uplift-status-trust"
        />
      )}
    </div>
  );
}

export function TrailStatusTrust({ trail, compact = false }) {
  const signal = statusTrustSignal(trail?.status, trail?.operational_status_verification);
  const tone = TONE[signal.tone] || TONE.unknown;

  if (compact) {
    return (
      <span
        data-testid="trail-status-trust-compact"
        className={"inline-flex items-center gap-1.5 text-[10px] font-bold " + tone.text}
        title={signal.label + " · " + signal.freshness.label}
      >
        <span className={"h-1.5 w-1.5 rounded-full " + tone.dot} />
        {translateLabel(signal.label)}
        {signal.uncertain && <span className="text-amber-300">· {translateLabel(signal.freshness.label)}</span>}
      </span>
    );
  }

  return (
    <StatusTrustCard
      label={translateLabel("Trail status")}
      status={trail?.status}
      verification={trail?.operational_status_verification}
      testId="trail-status-trust"
    />
  );
}
